import type { FC } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'

import { useRouter } from 'next/router'

import type { FieldValues } from 'react-hook-form'

import { AnimatePresence, m } from 'framer-motion'

import Loader from '@segment/matcha/components/Loader'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import GetAccessForm from '@segment/templates/Forms/GetAccessForm'

import { fetchProfileCookie } from '@segment/api/staticSiteBackend/segmentProfilesApi'

import { verifyUserHasAccess } from '../../helper/verifyUserHasAccess'

const copy = {
  heading: `Get started with Growth Masters`,
  description: `Sharpen your PM skills, be inspired by experts and learn from your peers. Sign up to receive full access to lessons, resources and live events.`,
}

const Form: FC = () => {
  const [isLoading, setLoading] = useState(false)
  const router = useRouter()
  const handleSubmit = async (data: FieldValues) => {
    setLoading(true)
    // Send and save profile cookie.
    const controller = new AbortController()
    await fetchProfileCookie(controller.signal, data.email)
    setTimeout(() => {
      const { page } = router.query
      if (page) {
        router.push(`/growth-masters/${page}/`)
      } else {
        router.push(`/growth-masters/`)
      }
    }, 1500)
  }

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h1" variant="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description tag="h2" variant="pLarge">
              {copy.description}
            </CopyText.Description>
          </CopyTextBase>
          <FormWrapper>
            <GetAccessForm
              onSubmit={(data) => verifyUserHasAccess(data)}
              onSuccess={(data) => handleSubmit(data)}
              customFormIdentifier="growth-masters-signup"
            />
            <AnimatePresence>
              {isLoading && (
                <StyledLoader
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, ease: `easeOut` }}
                >
                  <Loader />
                </StyledLoader>
              )}
            </AnimatePresence>
          </FormWrapper>
        </Section.Copy>
      </StyledSection>
    </Wrapper>
  )
}

const StyledSection = styled(SectionBase)`
  max-width: 880px;
  margin: 0 auto;

  ${Section.Wrapper} {
    padding-top: 20px;
    padding-bottom: 100px;
    gap: 0;

    ${({ theme }) => theme.breakpoints.down(`md`)} {
      padding-bottom: 60px;
    }
  }
`

const Wrapper = styled.div`
  padding-top: 80px;
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const FormWrapper = styled.div`
  max-width: 420px;
  min-height: 440px;
  margin: 0 auto;
  padding-top: 40px;
  display: flex;
  align-items: flex-start;
  form {
    p {
      text-align: center;
    }
  }
`

const StyledLoader = styled(m.div)``

export default Form
